import { FullInstrumentResponse } from 'app/modules/instruments/responses';
import { INSTRUMENT_QUERY_KEYS } from 'app/modules/instruments/queries/keys';
import { get, getWithParsedError } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectDLEnabledForInstrumentDetail } from 'app/shared/featureFlags/selectors';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';
import {
  getLoadingFromColdStorageStateForCGDO,
  getColdStorageRetryWaitTime,
  COLD_STORAGE_MAX_RETRIES,
  isCGDOLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';
import { useCGDOProgressiveData } from 'app/shared/hooks/useCGDOProgressiveData';
import { useRef, useMemo, useEffect } from 'react';

type InstrumentDetailsResponse =
  | FullInstrumentResponse
  | (FullInstrumentResponse & CGDOLoadingResponse)
  | CGDOLoadingResponse;

export const useInstrumentDetails = <TSelect = FullInstrumentResponse>(
  instrumentId: string,
  select: (data: FullInstrumentResponse) => TSelect = (data) => data as TSelect,
  enabled: boolean = true,
) => {
  const useCacheEndpoint = useSelector(selectDLEnabledForInstrumentDetail);
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<InstrumentDetailsResponse>({
    queryFn: async () => {
      if (useCacheEndpoint) {
        return getWithParsedError<InstrumentDetailsResponse>(
          `/instruments/cached/${encodeURIComponent(instrumentId)}`,
        );
      }
      return get<FullInstrumentResponse>(
        `/instruments/${encodeURIComponent(instrumentId)}`,
      );
    },
    queryKey: INSTRUMENT_QUERY_KEYS.getInstrumentDetails(
      instrumentId,
      useCacheEndpoint,
    ),
    staleTime: 10000,
    enabled: Boolean(instrumentId) && enabled,
    refetchInterval: ({ state: { data } }) => {
      if (useCacheEndpoint) {
        if (
          data &&
          isCGDOLoadingResponse(data) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      getLoadingFromColdStorageStateForCGDO(
        instrumentId,
        query.data && isCGDOLoadingResponse(query.data)
          ? query.data.data_loader
          : undefined,
      ),
    [instrumentId, query.data],
  );

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [instrumentId]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isCGDOLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  const { data, isLoading } = useCGDOProgressiveData(
    query.data,
    query.isLoading,
    query.isError,
    select,
  );

  return {
    // This is still accesible if needed for whatever reason
    query,
    // The following values already take into account the cold storage loading state
    data,
    isLoading,
    coldStorageLoading,
  };
};
